import React from 'react';
import PropTypes from 'prop-types';
import { useLabels, usePrefs } from 'ws-scripts/modules/common';
import { isPrefEnabled } from '../utils/prefUtils';

/**
 * ContactList — renders department phone rows and a Directions link.
 * Manages its own display prefs (contactLayout, contactShowIcon) and labels.
 */
const ContactList = ({ departments, mapsQuery, accountId }) => {
	const labels = useLabels();
	const { contactLayout, contactShowIcon } = usePrefs();
	// contactLayout is a string enum pref ('vertical'|'horizontal'), so direct
	// equality is correct here — isPrefEnabled() is for boolean prefs only.
	const isVertical = contactLayout === 'vertical';
	const showIcon = isPrefEnabled(contactShowIcon);

	const callLabel = labels.get('CALL');
	const directionsLabel = labels.get('DIRECTIONS');

	return (
		<ul
			className={[
				'ws-nav-drawer__contact-list',
				'list-unstyled',
				'm-0',
				isVertical ? 'ws-nav-drawer__contact-list--vertical' : '',
				!showIcon ? 'ws-nav-drawer__contact-list--no-icon' : ''
			]
				.filter(Boolean)
				.join(' ')}
		>
			{departments.map((department) => (
				<li key={department.label} className="w-100">
					<a
						href={`tel:${department.phone}`}
						data-phone-ref={department.type ?? department.label}
						data-account-ref={accountId ?? ''}
						data-drawer-phone="true"
						className="ws-nav-drawer__contact-link d-flex align-items-center justify-content-between w-100 py-4"
						aria-label={`${callLabel} ${department.label}`}
					>
						<span
							className="ws-nav-drawer__contact-inner"
							data-testid="contact-inner"
						>
							<span className="font-weight-bold ddc-font-size-base">
								{department.label}
							</span>
							<span
								className="ws-nav-drawer__contact-phone ddc-font-size-small"
								data-testid="contact-phone"
							>
								{department.phone}
							</span>
						</span>
						<i
							className={
								'ddc-icon ddc-icon-call-outline-thin ws-nav-drawer__nav-item-icon' +
								' ddc-font-size-large d-flex flex-shrink-0 align-items-center' +
								' justify-content-center'
							}
							aria-hidden="true"
						/>
					</a>
				</li>
			))}
			{mapsQuery && (
				<li className="w-100">
					<a
						href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
							mapsQuery
						)}`}
						target="_blank"
						rel="noopener noreferrer"
						className="ws-nav-drawer__contact-link d-flex align-items-center justify-content-between w-100 py-4"
						aria-label={`${directionsLabel}, ${labels.get(
							'OPENS_IN_NEW_TAB'
						)}`}
					>
						<span className="ws-nav-drawer__contact-inner">
							<span className="font-weight-bold ddc-font-size-base">
								{directionsLabel}
							</span>
						</span>
						<i
							className={
								'ddc-icon ddc-icon-map-outline-thin ws-nav-drawer__nav-item-icon' +
								' ddc-font-size-large d-flex flex-shrink-0 align-items-center' +
								' justify-content-center'
							}
							aria-hidden="true"
						/>
					</a>
				</li>
			)}
		</ul>
	);
};

ContactList.propTypes = {
	departments: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string,
			phone: PropTypes.string,
			type: PropTypes.string
		})
	).isRequired,
	mapsQuery: PropTypes.string,
	accountId: PropTypes.string
};

export default ContactList;
